import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { motionDurations, motionEasings } from '@/lib/motion-tokens';

// Enhanced AnimatePresence with better defaults
export const EnhancedAnimatePresence: React.FC<{
  children: React.ReactNode;
  mode?: "sync" | "wait" | "popLayout";
  initial?: boolean;
  custom?: any;
}> = ({ children, mode = "wait", initial = true, custom }) => {
  return (
    <AnimatePresence mode={mode} initial={initial} custom={custom}>
      {children}
    </AnimatePresence>
  );
};

// Smooth slide-in animation
export const SlideIn: React.FC<{
  children: React.ReactNode;
  direction?: "up" | "down" | "left" | "right";
  delay?: number;
  duration?: number;
  className?: string;
}> = ({
  children,
  direction = "up",
  delay = 0,
  duration = 0.5,
  className = ""
}) => {
  const directionVariants = {
    up: { y: 20 },
    down: { y: -20 },
    left: { x: 20 },
    right: { x: -20 }
  };

  return (
    <motion.div
      initial={{ opacity: 0, ...directionVariants[direction] }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{
        duration,
        delay,
        ease: motionEasings.premium,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Scale animation with spring physics
export const ScaleIn: React.FC<{
  children: React.ReactNode;
  delay?: number;
  duration?: number;
  scale?: number;
  className?: string;
}> = ({
  children,
  delay = 0,
  duration = 0.5,
  scale = 0.9,
  className = ""
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration,
        delay,
        ease: motionEasings.premium,
        type: "spring",
        stiffness: 300,
        damping: 30
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Stagger container for children animations
export const StaggerContainer: React.FC<{
  children: React.ReactNode;
  staggerDelay?: number;
  className?: string;
}> = ({
  children,
  staggerDelay = 0.1,
  className = ""
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: motionEasings.premium }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={className}
    >
      {React.Children.map(children, (child) => (
        <motion.div variants={itemVariants}>
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
};

// Hover lift effect
export const HoverLift: React.FC<{
  children: React.ReactNode;
  liftAmount?: number;
  className?: string;
}> = ({
  children,
  liftAmount = 8,
  className = ""
}) => {
  return (
    <motion.div
      whileHover={{ y: -liftAmount }}
      transition={{ duration: motionDurations.micro, ease: motionEasings.easeOut }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Magnetic hover effect
export const MagneticHover: React.FC<{
  children: React.ReactNode;
  strength?: number;
  className?: string;
}> = ({
  children,
  strength = 0.3,
  className = ""
}) => {
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const x = (e.clientX - centerX) * strength;
    const y = (e.clientY - centerY) * strength;
    setMousePosition({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 0, y: 0 });
  };

  return (
    <motion.div
      animate={{ x: mousePosition.x, y: mousePosition.y }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Morphing shape animation
export const MorphingShape: React.FC<{
  children: React.ReactNode;
  morphTo?: string;
  className?: string;
}> = ({
  children,
  morphTo,
  className = ""
}) => {
  return (
    <motion.div
      whileHover={{
        borderRadius: morphTo === "circle" ? "50%" : morphTo === "square" ? "0%" : "8px",
        scale: 1.05
      }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Text reveal animation
export const TextReveal: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({
  children,
  delay = 0,
  className = ""
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.6,
        delay,
        ease: motionEasings.premium
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Gradient shift animation
export const GradientShift: React.FC<{
  children: React.ReactNode;
  colors?: string[];
  className?: string;
}> = ({
  children,
  colors = ["from-purple-600", "to-indigo-600"],
  className = ""
}) => {
  return (
    <motion.div
      className={`bg-gradient-to-r ${colors.join(" ")} bg-clip-text text-transparent ${className}`}
      animate={{
        backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        ease: "linear"
      }}
      style={{
        backgroundSize: "200% 200%",
      }}
    >
      {children}
    </motion.div>
  );
};

// Pulse glow effect - more subtle and rounded
export const PulseGlow: React.FC<{
  children: React.ReactNode;
  color?: string;
  intensity?: number;
  className?: string;
}> = ({
  children,
  color = "purple",
  intensity = 0.3, // Reduced default intensity
  className = ""
}) => {
  const colorMap: Record<string, string> = {
    purple: "147, 51, 234",
    magenta: "236, 72, 153",
    blue: "59, 130, 246",
    green: "34, 197, 94"
  };

  const rgbaColor = colorMap[color] || colorMap.purple;

  return (
    <motion.div
      className={`relative ${className}`}
      animate={{
        boxShadow: [
          `0 0 0 0 rgba(${rgbaColor}, 0)`,
          `0 0 20px rgba(${rgbaColor}, ${intensity * 0.5})`, // Larger, more diffuse glow
          `0 0 0 0 rgba(${rgbaColor}, 0)`
        ]
      }}
      transition={{
        duration: 3, // Slower pulse
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      {children}
    </motion.div>
  );
};

// Elastic bounce animation
export const ElasticBounce: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({
  children,
  className = ""
}) => {
  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 10,
        mass: 1
      }}
      whileHover={{
        scale: 1.1,
        transition: { type: "spring", stiffness: 400, damping: 10 }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Floating animation
export const Floating: React.FC<{
  children: React.ReactNode;
  intensity?: number;
  speed?: number;
  className?: string;
}> = ({
  children,
  intensity = 10,
  speed = 2,
  className = ""
}) => {
  return (
    <motion.div
      animate={{
        y: [-intensity, intensity, -intensity],
      }}
      transition={{
        duration: speed,
        repeat: Infinity,
        ease: "easeInOut"
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};


// ============================================================================
// INNER SANCTUARY MOTION PRIMITIVES (Magenta/Cyan Theme)
// ============================================================================

// Inner Sanctuary Hover lift effect (magenta/cyan theme)
export const InnerSanctuaryHoverLift: React.FC<{
  children: React.ReactNode;
  liftAmount?: number;
  className?: string;
}> = ({
  children,
  liftAmount = 8,
  className = ""
}) => {
  return (
    <motion.div
      whileHover={{ y: -liftAmount }}
      transition={{ duration: motionDurations.micro, ease: motionEasings.easeOut }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Inner Sanctuary Gradient shift animation (magenta/cyan theme)
export const InnerSanctuaryGradientShift: React.FC<{
  children: React.ReactNode;
  colors?: string[];
  className?: string;
}> = ({
  children,
  colors = ["from-magenta-600", "to-cyan-600"],
  className = ""
}) => {
  return (
    <motion.div
      className={`bg-gradient-to-r ${colors.join(" ")} bg-clip-text text-transparent ${className}`}
      animate={{
        backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        ease: "linear"
      }}
      style={{
        backgroundSize: "200% 200%",
      }}
    >
      {children}
    </motion.div>
  );
};

// Inner Sanctuary Pulse glow effect (magenta theme)
export const InnerSanctuaryPulseGlow: React.FC<{
  children: React.ReactNode;
  color?: string;
  intensity?: number;
  className?: string;
}> = ({
  children,
  intensity = 0.5,
  className = ""
}) => {
  return (
    <motion.div
      className={`relative ${className}`}
      animate={{
        boxShadow: [
          `0 0 0 0 rgba(236, 72, 153, 0)`,
          `0 0 0 10px rgba(236, 72, 153, ${intensity})`,
          `0 0 0 0 rgba(236, 72, 153, 0)`
        ]
      }}
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      {children}
    </motion.div>
  );
};


// Inner Sanctuary Floating animation (cyan theme)
export const InnerSanctuaryFloating: React.FC<{
  children: React.ReactNode;
  intensity?: number;
  speed?: number;
  className?: string;
}> = ({
  children,
  intensity = 10,
  speed = 2,
  className = ""
}) => {
  return (
    <motion.div
      animate={{
        y: [-intensity, intensity, -intensity],
      }}
      transition={{
        duration: speed,
        repeat: Infinity,
        ease: "easeInOut"
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Inner Sanctuary Morphing shape animation (magenta/cyan theme)
export const InnerSanctuaryMorphingShape: React.FC<{
  children: React.ReactNode;
  morphTo?: string;
  className?: string;
}> = ({
  children,
  morphTo,
  className = ""
}) => {
  return (
    <motion.div
      whileHover={{
        borderRadius: morphTo === "circle" ? "50%" : morphTo === "square" ? "0%" : "8px",
        scale: 1.05
      }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};
